import React, { createContext, useContext, ReactNode, useState, useEffect, useMemo } from 'react';
import { Promotion } from '@/components/business/onboarding/types/promotionTypes'; // Corrected import path
import { useBusinessOperations } from '@/context/schema/useBusinessOperations'; // Changed import
import { useFirebase } from '@/context/FirebaseProvider';
import { Business, promotionSchema as GoCampaignPromotionSchema } from '@/schemas/business'; // Assuming Business schema, import Zod schema for promotions
import { z } from 'zod'; // Import z

// Define the context type
interface PromotionStateContextType {
  businessId: string;
  promotions: Promotion[];
  showAll: boolean;
  hasPromotions: boolean;
  isLoading: boolean;
  setShowAll: (show: boolean) => void;
  addPromotion: (promotion: Promotion) => Promise<boolean>; // Returns success status
  updatePromotion: (promotionId: string, updates: Partial<Promotion>) => void;
  removePromotion: (promotionId: string) => void;
  togglePromotionEnabled: (promotionId: string, enabled: boolean) => void;
  // handleGroupReorder: (groups: any) => void; // Old: Type for groups might need to be more specific
  handleReorderInGroup: (groupId: string, sourceIndex: number, destinationIndex: number) => void; // New
  setFullPromotionList: (updatedPromotions: Promotion[]) => void; // New for direct list setting if needed
  saveChanges: () => Promise<boolean>;
  removePromotionFromService: (promotionId: string, serviceId: string) => void; // Added
}

// Create context with default values
const PromotionStateContext = createContext<PromotionStateContextType>({
  businessId: '',
  promotions: [],
  showAll: false,
  hasPromotions: false,
  isLoading: false,
  setShowAll: () => {},
  addPromotion: async () => false,
  updatePromotion: () => {},
  removePromotion: () => {},
  togglePromotionEnabled: () => {},
  // handleGroupReorder: () => {}, // Old
  handleReorderInGroup: () => {}, // New
  setFullPromotionList: () => {}, // New
  saveChanges: async () => false,
  removePromotionFromService: () => {}, // Added placeholder
});

export const usePromotionState = () => useContext(PromotionStateContext);

interface PromotionStateProviderProps {
  businessId: string;
  children: ReactNode;
  onSectionComplete?: () => void; // Optional callback
}

export const PromotionStateProvider: React.FC<PromotionStateProviderProps> = ({
  businessId,
  children,
  onSectionComplete,
  // legacyPromotionData, // Removed prop
}) => {
  const { userData, businesses } = useFirebase(); // Added userData
  const business = useMemo(() => businesses.find(b => b.id === businessId), [businesses, businessId]);
  // Changed to useBusinessOperations and destructure isLoading correctly
  const { saveGoCampaignSection, isLoading } = useBusinessOperations(userData); 

  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  useEffect(() => {
    console.log("PromotionStateProvider: Initializing promotions for businessId:", businessId);
    const gocampaignPromotions = (business?.gocampaign as any)?.promotions as Promotion[] | undefined;
    const gocampaignShowAll = (business?.gocampaign as any)?.promotionsShowAll as boolean | undefined;

    let initialPromotions: Promotion[] = [];
    let initialShowAll = false;

    if (gocampaignPromotions && Array.isArray(gocampaignPromotions)) {
      console.log("PromotionStateProvider: Loading from gocampaign.promotions:", gocampaignPromotions);
      initialPromotions = gocampaignPromotions;
      initialShowAll = gocampaignShowAll ?? false; 
    } else {
      console.log("PromotionStateProvider: No business.gocampaign.promotions found or not an array, using defaults.");
      // Defaults are already set by useState if nothing is loaded.
    }
    
    setPromotions(initialPromotions);
    setShowAll(initialShowAll);
    setHasUnsavedChanges(false); // Reset on initialization
  }, [business, businessId]); // Removed legacyPromotionData from dependencies

  const addPromotionInternal = (promotion: Promotion): void => {
    setPromotions(prev => [...prev, { ...promotion, id: promotion.id || `promo_${Date.now()}` }]);
    setHasUnsavedChanges(true);
  };
  
  const updatePromotionInternal = (promotionId: string, updates: Partial<Promotion>): void => {
    setPromotions(prev => prev.map(p => p.id === promotionId ? { ...p, ...updates } : p));
    setHasUnsavedChanges(true);
  };

  const removePromotionInternal = (promotionId: string): void => {
    setPromotions(prev => prev.filter(p => p.id !== promotionId));
    setHasUnsavedChanges(true);
  };

  const togglePromotionEnabledInternal = (promotionId: string, enabled: boolean): void => {
    updatePromotionInternal(promotionId, { enabled });
  };
  
  // Renamed from handleGroupReorderInternal
  const setFullPromotionListInternal = (updatedPromotions: Promotion[]): void => {
    setPromotions(updatedPromotions);
    setHasUnsavedChanges(true);
  };

  const handleReorderInGroupInternal = (groupId: string, sourceIndex: number, destinationIndex: number): void => {
    setPromotions(prevPromotions => {
      const allPromosCopy = [...prevPromotions];
      
      // Extract promotions for the specific group
      const groupPromotions = allPromosCopy.filter(p => p.serviceGroupId === groupId || (groupId === 'all-services' && p.serviceId === 'all-services'));
      
      // Remove these from the main list temporarily to re-insert later
      const otherPromotions = allPromosCopy.filter(p => !(p.serviceGroupId === groupId || (groupId === 'all-services' && p.serviceId === 'all-services')));
      
      if (groupPromotions.length === 0) return prevPromotions; // Should not happen if called correctly

      const [removed] = groupPromotions.splice(sourceIndex, 1);
      groupPromotions.splice(destinationIndex, 0, removed);

      // Reconstruct the full list: other promotions first, then the reordered group.
      // This simple concatenation might mess up overall order if groups are interleaved.
      // A more robust way is to map original promotions, update the ones in the group, and keep others.
      // For now, let's assume promotions are mostly contiguous per group or this simplification is acceptable.
      // A better approach would be to find the original indices of the group items and replace them.

      // Let's try a more robust update:
      const newPromotionsList = prevPromotions.map(p => {
        // Find if this promotion is part of the group that was reordered
        const groupMatch = p.serviceGroupId === groupId || (groupId === 'all-services' && p.serviceId === 'all-services');
        if (groupMatch) {
          // This item will be replaced by one from the reordered `groupPromotions` list
          // We need to ensure we pick the correct one based on its new position.
          // This mapping approach is tricky with reordering.
          // A simpler way:
          // 1. Create a map of old promotions for easy lookup.
          // 2. The `groupPromotions` now holds the reordered items for *that group*.
          // 3. We need to integrate this back into the `allPromosCopy`.
          return null; // Placeholder, will be filtered out
        }
        return p;
      }).filter(p => p !== null) as Promotion[];

      // This is still not quite right. The reordering logic should operate on the full `prevPromotions` list
      // by identifying items of the group, reordering them *within their original positions relative to other groups*.
      // The current `groupPromotions` only contains items of that group.

      // Corrected approach:
      const reorderedFullList = Array.from(prevPromotions);
      const itemsToReorder = reorderedFullList.filter(p => p.serviceGroupId === groupId || (groupId === 'all-services' && p.serviceId === 'all-services'));
      
      if (itemsToReorder.length === 0) return prevPromotions;

      const [movedItem] = itemsToReorder.splice(sourceIndex, 1);
      itemsToReorder.splice(destinationIndex, 0, movedItem);

      // Now, replace the original items in reorderedFullList with items from itemsToReorder
      let reorderedIdx = 0;
      for (let i = 0; i < reorderedFullList.length; i++) {
        const currentItemIsPartOfGroup = reorderedFullList[i].serviceGroupId === groupId || (groupId === 'all-services' && reorderedFullList[i].serviceId === 'all-services');
        if (currentItemIsPartOfGroup) {
          if (reorderedIdx < itemsToReorder.length) {
            reorderedFullList[i] = itemsToReorder[reorderedIdx];
            reorderedIdx++;
          } else {
            // This case should ideally not be reached if logic is correct
            console.error("Mismatch in reordered items length");
          }
        }
      }
      setHasUnsavedChanges(true);
      return reorderedFullList;
    });
  };

  const saveChangesInternal = async (): Promise<boolean> => {
    console.log('Saving promotions to gocampaign.promotions:', promotions, 'ShowAll:', showAll);
    
    // Validate each promotion against the Zod schema before saving
    const validatedPromotions = promotions.map(p => GoCampaignPromotionSchema.parse(p));

    // Changed to use saveGoCampaignSection
    // The 'promotions' section in GoCampaignObject includes both the list and 'promotionsShowAll'
    // However, saveGoCampaignSection expects data for a single key of GoCampaignObject.
    // We need to save 'promotions' list and 'promotionsShowAll' potentially in two calls or adjust saveGoCampaignSection.
    // For now, let's assume we save them together if they are part of the same conceptual "promotions" settings.
    // The goCampaignSchema in business.ts has `promotions: z.array(promotionSchema)` and `promotionsShowAll: z.boolean()`
    // These are separate top-level keys in the goCampaignSchema.
    // So, we should make two separate calls or update saveGoCampaignSection to handle a partial GoCampaignObject.

    // Option 1: Two separate calls (simpler with current saveGoCampaignSection)
    let successPromotions = false;
    let successShowAll = false;
    try {
      successPromotions = await saveGoCampaignSection(businessId, 'promotions', validatedPromotions);
      successShowAll = await saveGoCampaignSection(businessId, 'promotionsShowAll', showAll);
    } catch (error) {
      console.error("Error saving promotions data:", error);
      // Handle error appropriately, maybe set an error state
      return false;
    }
    
    const overallSuccess = successPromotions && successShowAll;

    if (overallSuccess) {
      setHasUnsavedChanges(false);
      if (onSectionComplete) onSectionComplete();
    }
    return overallSuccess; // Return overallSuccess
  };

  const contextValue: PromotionStateContextType = {
    businessId,
    promotions,
    showAll,
    hasPromotions: promotions.length > 0,
    isLoading, // Use isLoading from useBusinessOperations
    setShowAll: (show) => { setShowAll(show); setHasUnsavedChanges(true); }, // Assuming changing showAll is a savable change
    addPromotion: async (promo) => { addPromotionInternal(promo); return true; }, // Simplified, assuming add is local first
    updatePromotion: updatePromotionInternal,
    removePromotion: removePromotionInternal,
    togglePromotionEnabled: togglePromotionEnabledInternal,
    // handleGroupReorder: handleGroupReorderInternal, // Old
    handleReorderInGroup: handleReorderInGroupInternal, // New
    setFullPromotionList: setFullPromotionListInternal, // New
    saveChanges: saveChangesInternal,
    removePromotionFromService: (promotionId: string, serviceId: string) => { // Added placeholder implementation
      console.warn(`removePromotionFromService called for promo ${promotionId} and service ${serviceId}, but not implemented yet.`);
      // Placeholder: In a real scenario, you might update the promotion's associated services list
      // updatePromotionInternal(promotionId, { associatedServices: currentServices.filter(s => s.id !== serviceId) });
      // setHasUnsavedChanges(true);
    },
  };
  
  return (
    <PromotionStateContext.Provider value={contextValue}>
      {children}
    </PromotionStateContext.Provider>
  );
};
